import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, Copy, FileJson, Loader2, RefreshCw, ShieldCheck } from 'lucide-react'
import { adminApi, type AdminContent, type ContentHierarchyNode, type ContentHierarchyResponse } from '@/api/admin'
import { dataPromotionApi, type DataPromotionExportResponse } from '@/api/dataPromotion'
import { printBookApi, type PrintBookDetail, type PrintBookListItem } from '@/api/printBook'

interface PromotionManifest {
  schemaVersion: 2
  promotionKey: string
  createdAt: string
  sourceEnvironment: 'staging'
  targetEnvironment: 'production'
  curriculumIds: number[]
  bookIds: number[]
  include: {
    learningConfigurations: true
    products: true
    pricing: true
    contentFeePlans: true
    printBooks: true
  }
}

interface AnalysisResult {
  manifest: PromotionManifest
  contentCount: number
  bookCandidates: PrintBookListItem[]
}

function flattenHierarchy(root: ContentHierarchyResponse): ContentHierarchyNode[] {
  const result: ContentHierarchyNode[] = []
  const stack: ContentHierarchyNode[] = [...root.children]

  while (stack.length > 0) {
    const node = stack.pop()
    if (!node) continue
    result.push(node)
    stack.push(...node.children)
  }

  return result
}

function buildPromotionKey(): string {
  return `lc-reference-${new Date().toISOString().slice(0, 10)}`
}

export function DataPromotionPage() {
  const [selectedCurriculumIds, setSelectedCurriculumIds] = useState<Set<number>>(() => new Set())
  const [selectedBookIds, setSelectedBookIds] = useState<Set<number>>(() => new Set())
  const [promotionKey, setPromotionKey] = useState(buildPromotionKey)
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [exportResult, setExportResult] = useState<DataPromotionExportResponse | null>(null)

  const curriculaQuery = useQuery({
    queryKey: ['admin', 'data-promotion', 'curricula'],
    queryFn: () => adminApi.getContents({ hierarchyLevel: 'curriculum', size: 500 }),
    select: (data) => data.content.filter((content: AdminContent) => content.origin === 'direct'),
  })

  const booksQuery = useQuery({
    queryKey: ['admin', 'data-promotion', 'books'],
    queryFn: printBookApi.getAll,
  })

  const selectedCurricula = useMemo(
    () => curriculaQuery.data?.filter((item) => selectedCurriculumIds.has(item.id)) ?? [],
    [curriculaQuery.data, selectedCurriculumIds],
  )

  const toggleCurriculum = (id: number) => {
    setSelectedCurriculumIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setAnalysis(null)
    setExportResult(null)
    setSelectedBookIds(new Set())
  }

  const toggleBook = (id: number) => {
    setSelectedBookIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setExportResult(null)
  }

  const analyze = async () => {
    if (selectedCurriculumIds.size === 0 || promotionKey.trim().length === 0) return
    setIsAnalyzing(true)
    setAnalysisError(null)
    setExportResult(null)

    try {
      const curriculumIds = Array.from(selectedCurriculumIds).sort((a, b) => a - b)
      const hierarchies = await Promise.all(curriculumIds.map((id) => adminApi.getContentHierarchy(id, true)))
      const nodes = hierarchies.flatMap(flattenHierarchy)
      const contentIds = new Set([...curriculumIds, ...nodes.map((node) => node.id)])
      const allBooks = booksQuery.data ?? []
      const bookDetails = await Promise.all(allBooks.map((book) => printBookApi.getById(book.id)))
      const detailById = new Map<number, PrintBookDetail>(bookDetails.map((detail) => [detail.id, detail]))
      const candidates = allBooks.filter((book) => {
        if (book.scopeContentId != null && contentIds.has(book.scopeContentId)) return true
        const detail = detailById.get(book.id)
        return detail?.modules.some(
          (module) => contentIds.has(module.moduleId) || module.lessons.some((lesson) => contentIds.has(lesson.lessonId)),
        ) ?? false
      })
      const candidateIds = new Set(candidates.map((book) => book.id))
      setSelectedBookIds(candidateIds)

      const manifest: PromotionManifest = {
        schemaVersion: 2,
        promotionKey: promotionKey.trim(),
        createdAt: new Date().toISOString(),
        sourceEnvironment: 'staging',
        targetEnvironment: 'production',
        curriculumIds,
        bookIds: Array.from(candidateIds).sort((a, b) => a - b),
        include: {
          learningConfigurations: true,
          products: true,
          pricing: true,
          contentFeePlans: true,
          printBooks: true,
        },
      }

      setAnalysis({
        manifest,
        contentCount: contentIds.size,
        bookCandidates: candidates,
      })
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : '승격 대상을 분석할 수 없습니다.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const finalizedManifest = analysis
    ? { ...analysis.manifest, bookIds: Array.from(selectedBookIds).sort((a, b) => a - b) }
    : null

  const exportManifest = async () => {
    if (!finalizedManifest) return
    setIsExporting(true)
    setAnalysisError(null)
    try {
      setExportResult(await dataPromotionApi.exportManifest({
        promotionKey: finalizedManifest.promotionKey,
        curriculumIds: finalizedManifest.curriculumIds,
        bookIds: finalizedManifest.bookIds,
        include: finalizedManifest.include,
      }))
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : '승격 manifest를 생성할 수 없습니다.')
    } finally {
      setIsExporting(false)
    }
  }

  const isLoading = curriculaQuery.isLoading || booksQuery.isLoading
  const loadError = curriculaQuery.error || booksQuery.error

  return (
    <main className="min-h-full bg-slate-50 p-6">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <header className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-sm">
          <div className="grid gap-6 p-7 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-amber-300">
                <ShieldCheck className="size-4" />
                Staging 전용 · Production 직접 쓰기 없음
              </div>
              <h1 className="text-3xl font-bold tracking-tight">LC 기준정보 승격 준비</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                지정한 커리큘럼의 전체 구조와 연결 교재, staging의 전체 깨처 콘텐츠를 검토 가능한 manifest로 생성합니다.
                실제 production 적용은 승인된 GitHub Actions에서만 수행됩니다.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-right">
              <div className="text-xs text-slate-400">선택된 커리큘럼</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{selectedCurriculumIds.size}</div>
            </div>
          </div>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-24">
            <Loader2 className="size-6 animate-spin text-slate-400" />
          </div>
        ) : loadError ? (
          <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle className="size-5" />
            커리큘럼 또는 교재 목록을 불러오지 못했습니다.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <h2 className="font-semibold text-slate-950">1. 커리큘럼 선택</h2>
                <p className="mt-1 text-sm text-slate-500">커리큘럼 빌더에서 생성한 direct 커리큘럼만 표시됩니다.</p>
              </div>
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                {curriculaQuery.data?.map((curriculum) => {
                  const checked = selectedCurriculumIds.has(curriculum.id)
                  return (
                    <label
                      key={curriculum.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                        checked ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCurriculum(curriculum.id)}
                        className="mt-1 size-4 accent-blue-600"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-900">{curriculum.title}</span>
                        <span className="mt-1 block text-xs text-slate-500">ID {curriculum.id} · {curriculum.status}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </section>

            <aside className="flex flex-col gap-4">
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="font-semibold text-slate-950">2. Manifest 설정</h2>
                <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="promotion-key">
                  Promotion key
                </label>
                <input
                  id="promotion-key"
                  value={promotionKey}
                  onChange={(event) => {
                    setPromotionKey(event.target.value)
                    setAnalysis(null)
                  }}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="button"
                  onClick={analyze}
                  disabled={selectedCurriculumIds.size === 0 || promotionKey.trim().length === 0 || isAnalyzing}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {isAnalyzing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  연결 데이터 분석
                </button>
                {analysisError ? <p className="mt-3 text-sm text-red-600">{analysisError}</p> : null}
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="font-semibold text-slate-950">선택 요약</h2>
                <div className="mt-3 flex flex-col gap-2 text-sm">
                  {selectedCurricula.length === 0 ? (
                    <p className="text-slate-500">커리큘럼을 선택하세요.</p>
                  ) : selectedCurricula.map((curriculum) => (
                    <div key={curriculum.id} className="flex items-center gap-2 text-slate-700">
                      <Check className="size-4 text-blue-600" />
                      <span className="truncate">{curriculum.title}</span>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        )}

        {analysis && finalizedManifest ? (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-semibold text-slate-950">3. 분석 결과와 교재 확인</h2>
                <p className="mt-1 text-sm text-slate-500">자동 후보에서 제외할 교재가 있으면 선택을 해제하세요.</p>
              </div>
              <button
                type="button"
                onClick={exportManifest}
                disabled={isExporting}
                className="flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {isExporting ? <Loader2 className="size-4 animate-spin" /> : <FileJson className="size-4" />}
                Manifest 확정·저장
              </button>
            </div>
            <div className="grid gap-4 border-b border-slate-200 p-5 sm:grid-cols-3">
              {[
                ['전체 콘텐츠', analysis.contentCount],
                ['교재 후보', analysis.bookCandidates.length],
                ['선택 교재', selectedBookIds.size],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-xl bg-slate-50 p-4">
                  <div className="text-xs font-medium text-slate-500">{label}</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-950">{value}</div>
                </div>
              ))}
            </div>
            <div className="border-b border-slate-200 bg-amber-50 px-5 py-3 text-sm text-amber-800">
              깨처 콘텐츠는 quetureId 기준으로 전체 동기화됩니다. direct/Notion 분류, production lesson 매칭,
              충돌 판정은 승인 전 ECS dry-run에서 수행됩니다.
            </div>
            <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
              {analysis.bookCandidates.length === 0 ? (
                <div className="col-span-full flex items-center gap-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                  <FileJson className="size-5" />
                  연결된 교재 후보가 없습니다.
                </div>
              ) : analysis.bookCandidates.map((book) => {
                const checked = selectedBookIds.has(book.id)
                return (
                  <label key={book.id} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleBook(book.id)}
                      className="mt-1 size-4 accent-blue-600"
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-900">{book.title}</span>
                      <span className="mt-1 block text-xs text-slate-500">
                        ID {book.id} · {book.moduleCount}개 모듈 · {book.lessonCount}개 레슨
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
            {exportResult ? (
              <div className="m-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <div className="flex items-center gap-2 font-semibold"><Check className="size-4" />Manifest 저장 완료</div>
                <dl className="mt-3 grid gap-2 font-mono text-xs">
                  <div><dt className="inline font-sans text-emerald-700">S3 URI: </dt><dd className="inline break-all">{exportResult.manifestUri}</dd></div>
                  <div><dt className="inline font-sans text-emerald-700">Checksum: </dt><dd className="inline break-all">{exportResult.checksum}</dd></div>
                  <div><dt className="inline font-sans text-emerald-700">깨처 콘텐츠: </dt><dd className="inline">{exportResult.quetureContentCount}건</dd></div>
                </dl>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(`${exportResult.manifestUri}\n${exportResult.checksum}`)}
                  className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 font-medium text-emerald-800"
                >
                  <Copy className="size-4" />실행 정보 복사
                </button>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </main>
  )
}
